import type { FaqEntry } from '../faq';

/**
 * Italian FAQ, translated from `es.ts`, which is the source.
 *
 * Product names follow the catalogs: `Redondeo Solidario` is "Arrotondamento Solidale" and
 * `entidades sociales` is "enti sociali". Informal second person, as the Spanish uses `tú`.
 */
export const faqEntriesIt: FaqEntry[] = [
	// MARK: 1. Per le aziende
	{
		block: 'companies',
		question: 'Che cos’è Worldcoo?',
		answer:
			'Worldcoo è un’azienda tecnologica che mette in contatto aziende, persone ed enti sociali per rendere possibili nuove forme di donazione e generare impatto sociale e ambientale. Dal 2012 sviluppiamo soluzioni che integrano la donazione nei canali di acquisto delle aziende e trasformano piccoli gesti quotidiani in occasioni di impatto.',
	},
	{
		block: 'companies',
		question: 'Quali soluzioni offre Worldcoo?',
		answer:
			'Il nostro obiettivo è facilitare l’integrazione della solidarietà nei diversi punti di contatto tra le aziende e i loro clienti o dipendenti. Worldcoo sviluppa diverse soluzioni di donazione che si adattano ai canali e alle esigenze di ogni azienda. Tra queste ci sono l’Arrotondamento Solidale, le donazioni in ecommerce, i portali di donazione e le soluzioni di donazione legate a buste paga o fatture.',
	},
	{
		block: 'companies',
		question: 'Che cos’è l’Arrotondamento Solidale?',
		answer:
			'L’Arrotondamento Solidale permette ai clienti di un’azienda di fare una piccola donazione al momento di concludere un acquisto. Il cliente può arrotondare l’importo del suo acquisto e donare la differenza a un progetto sociale o ambientale selezionato.',
	},
	{
		block: 'companies',
		question: 'Quanto costa integrare Worldcoo?',
		answer:
			'Analizziamo ogni caso in modo personalizzato, tenendo conto delle esigenze dell’azienda, della soluzione che si vuole implementare e del tipo di integrazione tecnologica necessaria. A seconda dell’operatività e dell’implementazione richieste, l’integrazione delle soluzioni di Worldcoo può essere gratuita.',
	},
	{
		block: 'companies',
		question: 'Devo cambiare il mio sistema di pagamento per integrare Worldcoo?',
		answer:
			'Worldcoo collabora con diversi fornitori tecnologici e sistemi di pagamento per integrare le sue soluzioni nei canali di ogni azienda. Il nostro team analizza ogni caso per individuare la migliore opzione di integrazione.',
	},
	{
		block: 'companies',
		question: 'In quali canali posso attivare Worldcoo?',
		answer:
			'Le soluzioni di Worldcoo possono essere integrate in diversi punti di contatto con il cliente, sia fisici sia digitali, e anche con i dipendenti:',
		bullets: [
			'Negozi e punti vendita fisici.',
			'Ecommerce.',
			'App.',
			'Portali di Donazione online',
			'Buste paga e fatture',
		],
		outro: 'La soluzione si adatta alle caratteristiche e alle esigenze di ogni azienda.',
	},
	{
		block: 'companies',
		question: 'Posso scegliere quanto dona il cliente?',
		answer:
			'Sì. Lavoriamo con ogni azienda per individuare progetti sociali o ambientali in linea con i suoi valori, obiettivi e priorità. Worldcoo propone una selezione di progetti che possono essere adatti a ciascuna azienda, ma la decisione finale su quale causa sostenere spetta sempre all’azienda.',
	},
	{
		block: 'companies',
		question: 'Posso scegliere quale causa o progetto sostenere?',
		answer:
			'Sì. Worldcoo lavora con le aziende per individuare progetti in linea con i loro valori, obiettivi di impatto e priorità sociali o ambientali. Worldcoo presenta una proposta di progetti che ritiene adatti a quella specifica azienda, ma è l’azienda ad avere l’ultima parola sul progetto scelto.',
	},
	{
		block: 'companies',
		question: 'Posso sostenere progetti locali?',
		answer:
			'Sì. In Worldcoo lavoriamo con enti sociali di dimensioni e ambiti di intervento diversi, incluse organizzazioni locali che realizzano progetti con un grande impatto nelle loro comunità. Uno dei nostri obiettivi è facilitare l’accesso ai finanziamenti anche per questi enti e dare visibilità all’importante lavoro che svolgono.',
	},
	{
		block: 'companies',
		question: 'Posso cambiare progetto?',
		answer:
			'Sì. Le campagne possono essere aggiornate quando un progetto raggiunge il suo obiettivo di finanziamento, quando termina o quando l’azienda decide di sostenere una nuova causa.',
	},
	{
		block: 'companies',
		question: 'Posso sapere quanto è stato raccolto?',
		answer:
			'Sì. Worldcoo offre strumenti di monitoraggio che permettono di conoscere l’andamento delle campagne e l’impatto generato, giorno per giorno e suddiviso per punto vendita.',
	},
	{
		block: 'companies',
		question: 'Posso comunicare i risultati della campagna?',
		answer:
			'Sì. Worldcoo fornisce informazioni sull’andamento delle campagne e sui progetti finanziati per facilitare la comunicazione dell’impatto generato.',
	},

	// MARK: 2. Per gli enti sociali
	{
		block: 'ngos',
		question: 'Come può il mio ente raccogliere fondi tramite Worldcoo?',
		answer:
			'Gli enti sociali possono presentare i loro progetti a Worldcoo per candidarsi a far parte delle campagne di raccolta attivate con le aziende partner. Il nostro team analizza ogni proposta e, quando si presenta un’opportunità di collaborazione, lavora con l’ente per avviare la campagna.',
	},
	{
		block: 'ngos',
		question: 'Che tipo di progetti finanzia Worldcoo?',
		answer: 'Lavoriamo con progetti di diversi ambiti sociali e ambientali, tra cui:',
		bullets: [
			'Infanzia e giovani.',
			'Inclusione sociale.',
			'Salute.',
			'Persone anziane.',
			'Disabilità.',
			'Ambiente.',
			'Cooperazione.',
			'Emergenze.',
			'Altri ambiti di impatto sociale.',
		],
	},
	{
		block: 'ngos',
		question: 'Quali requisiti deve avere un ente per collaborare con Worldcoo?',
		answer:
			'Gli enti devono rispettare i criteri di selezione stabiliti da Worldcoo e presentare la documentazione necessaria per attestare la propria costituzione, attività e situazione giuridica. Devono inoltre essere in grado di emettere le relative ricevute di donazione.',
		outro: 'Puoi consultare i criteri di selezione di Worldcoo per conoscere tutti i requisiti.',
		link: { label: 'Criteri di selezione', target: 'selection-criteria' },
	},
	{
		block: 'ngos',
		question: 'Come iniziare a collaborare?',
		answer: 'Puoi avviare il processo compilando il modulo di registrazione con le informazioni di base del tuo ente.',
		link: { label: 'Vai al modulo di registrazione', target: 'ngo-signup' },
	},
	{
		block: 'ngos',
		question: 'Posso presentare un progetto specifico?',
		answer:
			'Sì. Worldcoo contribuisce a finanziare progetti specifici degli enti sociali, non a finanziare gli enti in generale. Puoi condividere con noi uno o più progetti che necessitano di finanziamento. Il nostro team ne valuterà l’aderenza ai criteri di collaborazione e cercherà le opportunità più adatte a ciascun progetto.',
	},
	{
		block: 'ngos',
		question: 'Possono collaborare enti piccoli o locali?',
		answer:
			'Sì. In Worldcoo vogliamo contribuire a far sì che anche gli enti locali e di dimensioni più ridotte possano accedere a nuove opportunità di finanziamento. Molte di queste organizzazioni svolgono un lavoro essenziale nelle loro comunità e generano un impatto diretto molto rilevante.',
	},
	{
		block: 'ngos',
		question: 'Come funziona il processo di scelta dei progetti?',
		answer:
			'Una volta entrato a far parte della comunità di Worldcoo, i tuoi progetti entrano nella nostra banca dati. Quando si presenta un’opportunità di collaborazione, proponiamo i progetti più adatti alle esigenze e ai criteri di ciascuna azienda, tenendo conto di aspetti come l’ambito territoriale e tematico, la sua strategia di CSR o il profilo dei suoi clienti.',
		outro:
			'La decisione finale su quale organizzazione collaborerà in ogni campagna spetta sempre all’azienda partner.',
	},
	{
		block: 'ngos',
		question: 'Una volta finanziato un progetto, come vengono gestiti i fondi raccolti?',
		answer:
			'Al termine della campagna di raccolta, Worldcoo gestisce il trasferimento del 100% dei fondi all’ente sociale beneficiario. Il processo avviene in due pagamenti:',
		bullets: [
			'Un primo pagamento del 50 % dell’importo raccolto al termine della campagna.',
			'Un secondo pagamento del restante 50 % una volta che l’ente presenta la relazione di monitoraggio e rendicontazione del progetto.',
		],
	},
	{
		block: 'ngos',
		question: 'Quali impegni assume un ente quando riceve un finanziamento?',
		answer:
			'Gli enti che ricevono fondi tramite Worldcoo si impegnano a rendicontare la realizzazione e l’impatto del progetto finanziato. A tal fine, devono presentare una relazione che includa informazioni su:',
		bullets: [
			'Lo svolgimento del progetto e le persone beneficiarie.',
			'I risultati e l’impatto ottenuti.',
			'Immagini o altra documentazione che permetta di seguire l’andamento del progetto.',
			'L’utilizzo e la destinazione dei fondi ricevuti.',
		],
	},
	{
		block: 'ngos',
		question: 'Quanto costa collaborare con Worldcoo per un ente sociale?',
		answer:
			'Worldcoo offre agli enti sociali servizi di raccolta, gestione e monitoraggio dei fondi, oltre all’accesso a nuove opportunità di collaborazione con le aziende. Per questi servizi Worldcoo applica una commissione sui fondi gestiti. Le condizioni vengono comunicate in modo trasparente a ogni ente prima dell’inizio di qualsiasi collaborazione.',
	},

	// MARK: 3. Per i donatori
	{
		block: 'donors',
		question: 'Che cos’è l’Arrotondamento Solidale?',
		answer:
			'L’Arrotondamento Solidale è un modo semplice di fare una piccola donazione mentre fai un acquisto. Al momento del pagamento puoi arrotondare l’importo del tuo acquisto e destinare quei centesimi a un progetto sociale o ambientale.',
	},
	{
		block: 'donors',
		question: 'Dove posso fare una donazione?',
		answer:
			'Puoi trovare le soluzioni di donazione di Worldcoo in diversi punti vendita e canali digitali delle nostre aziende partner.',
	},
	{
		block: 'donors',
		question: 'Quanto dono?',
		answer:
			'L’importo della donazione dipende dalla soluzione e dalla campagna a cui partecipi. Nel caso dell’Arrotondamento Solidale puoi arrotondare l’importo del tuo acquisto e donare la differenza. In altre campagne puoi fare una donazione di un importo fisso stabilito in precedenza. Prima di confermare la donazione potrai sempre conoscere l’importo che stai per versare.',
	},
	{
		block: 'donors',
		question: 'Posso decidere se donare?',
		answer:
			'Sì. La partecipazione all’Arrotondamento Solidale è volontaria e anonima. Prima di completare la donazione, il sistema ti informa dell’importo che puoi versare.',
	},
	{
		block: 'donors',
		question: 'A quale progetto è destinata la mia donazione?',
		answer:
			'La tua donazione è destinata al progetto associato alla campagna a cui stai partecipando. Puoi consultare informazioni sul progetto, sull’ente responsabile e sull’andamento della campagna attraverso i canali predisposti da Worldcoo e da ciascuna azienda.',
	},
	{
		block: 'donors',
		question: 'Come posso sapere quanto è stato raccolto?',
		answer:
			'Worldcoo mette a disposizione informazioni sugli obiettivi di ogni campagna e sul suo andamento, per facilitare il monitoraggio delle donazioni e dell’impatto generato.',
	},
	{
		block: 'donors',
		question: 'Che cosa succede al mio denaro dopo la donazione?',
		answer:
			'Una volta effettuata la donazione, Worldcoo gestisce e monitora i fondi raccolti durante la campagna. Al termine della campagna, i fondi vengono destinati all’ente sociale responsabile del progetto per contribuire al suo finanziamento. Successivamente l’ente rendiconta lo svolgimento e l’impatto ottenuto grazie ai fondi raccolti.',
	},
	{
		block: 'donors',
		question: 'Posso ottenere una ricevuta della mia donazione?',
		answer:
			'Sì. Puoi richiedere la tua ricevuta di donazione su app.worldcoo.com. Per farlo dovrai conservare lo scontrino o la prova della tua donazione, dove troverai il codice di donazione necessario per identificarla.',
	},
	{
		block: 'donors',
		question: 'Che cos’è il codice di donazione?',
		answer:
			'Il codice di donazione è un identificativo univoco associato al tuo contributo. Lo trovi sullo scontrino o sulla prova di donazione e puoi usarlo per registrare la tua donazione su app.worldcoo.com e richiedere, quando previsto, la tua ricevuta di donazione.',
	},
	{
		block: 'donors',
		question: 'Come posso conoscere tutte le donazioni che ho fatto?',
		answer:
			'Su app.worldcoo.com puoi registrare e consultare le donazioni che hai fatto. La piattaforma ti permette di avere uno storico delle tue donazioni e di richiedere le relative ricevute.',
	},
	{
		block: 'donors',
		question: 'Posso annullare o chiedere il rimborso di una donazione?',
		answer:
			'Sì. Puoi chiedere il rimborso di una donazione. Il denaro donato è gestito direttamente da Worldcoo, quindi sarà Worldcoo a occuparsi del rimborso dell’importo donato su richiesta del cliente.',
		outro: 'Vai nella nostra sezione contatti e scrivici illustrando il tuo caso.',
		link: { label: 'Contattaci', target: 'contact' },
	},

	// MARK: 4. Trasparenza e sicurezza
	{
		block: 'transparency',
		question: 'Come garantisce Worldcoo la trasparenza delle donazioni?',
		answer:
			'La trasparenza è uno dei pilastri del nostro modello. Worldcoo monitora le campagne e mette a disposizione informazioni sugli importi raccolti, sui progetti finanziati e sugli enti partecipanti. Inoltre pubblichiamo periodicamente informazioni sulla nostra attività e sull’impatto generato.',
	},
	{
		block: 'transparency',
		question: 'Quanto denaro arriva agli enti sociali?',
		answer:
			'Il 100 % delle donazioni raccolte in ogni campagna è destinato al progetto e all’ente sociale responsabile della sua realizzazione. Successivamente l’ONG effettua un pagamento a Worldcoo per i servizi prestati.',
	},
	{
		block: 'transparency',
		question: 'Quanti progetti ha finanziato Worldcoo?',
		answer:
			'Dalla nostra nascita, Worldcoo ha contribuito a finanziare più di 1.000 progetti sociali e ambientali, in collaborazione con centinaia di enti.',
	},
	{
		block: 'transparency',
		question: 'Quante donazioni sono state fatte tramite Worldcoo?',
		answer:
			'Milioni di persone hanno partecipato alle campagne di Worldcoo e sono state effettuate più di 100 milioni di microdonazioni.',
	},
	{
		block: 'transparency',
		question: 'Dove posso consultare l’impatto di Worldcoo?',
		answer:
			'Puoi consultare informazioni sulle campagne, sui progetti e sull’impatto generato da Worldcoo sul nostro sito. Pubblichiamo inoltre periodicamente informazioni sulla nostra attività e il nostro bilancio di impatto.',
	},
	{
		block: 'transparency',
		question: 'È sicuro donare tramite Worldcoo?',
		answer:
			'Sì. Le soluzioni di Worldcoo sono integrate nei sistemi di pagamento delle nostre aziende partner e sono sviluppate secondo gli standard di sicurezza applicabili. Worldcoo monitora inoltre le campagne e i fondi raccolti per garantire una gestione trasparente delle donazioni.',
	},
	{
		block: 'transparency',
		question: 'Worldcoo conserva i dati della mia carta?',
		answer: 'Worldcoo non ha bisogno di conservare i dati completi della tua carta per permetterti di donare.',
	},
	{
		block: 'transparency',
		question: 'Come vengono utilizzati i miei dati personali?',
		answer:
			'I dati personali sono trattati in conformità con la nostra Informativa sulla privacy e con la normativa vigente in materia di protezione dei dati.',
	},

	// MARK: 5. Su Worldcoo
	{
		block: 'about',
		question: 'Quando è nata Worldcoo?',
		answer:
			'Worldcoo è nata nel 2012 con l’obiettivo di usare la tecnologia per facilitare il finanziamento di progetti sociali e ambientali.',
	},
	{
		block: 'about',
		question: 'Dove opera Worldcoo?',
		answer:
			'Worldcoo lavora con aziende ed enti sociali in mercati diversi e canalizza fondi verso progetti sia nazionali sia internazionali.',
	},
	{
		block: 'about',
		question: 'Worldcoo è una ONG?',
		answer:
			'No. Worldcoo è un’azienda tecnologica specializzata in soluzioni di microdonazione. Il nostro obiettivo è mettere in contatto aziende, persone ed enti sociali per facilitare la collaborazione e generare impatto.',
	},
	{
		block: 'about',
		question: 'Worldcoo è un’azienda B Corp?',
		answer:
			'Sì. Worldcoo possiede la certificazione B Corp, che riconosce l’impegno delle aziende verso elevati standard di impatto sociale e ambientale, trasparenza e responsabilità.',
	},
	{
		block: 'about',
		question: 'Perché Worldcoo?',
		answer:
			'Perché crediamo che donare dovrebbe essere semplice. In Worldcoo usiamo la tecnologia per mettere in contatto aziende, persone ed enti sociali e trasformare milioni di piccoli gesti quotidiani in occasioni di impatto. Dal 2012 lavoriamo per rendere la collaborazione sociale sempre più semplice, accessibile e trasparente.',
	},
];
